import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  ReservationApi,
  ReservationListResponse,
} from "../services/ReservationService";

export interface ExtraTag {
  sender: string;
  company: string;
  to_date: string;
  to_time: string;
  from_date: string;
  from_time: string;
  room_type: string;
  room_category: string;
  room_type_label: string;
  room_category_label: string;
}

export interface Extra {
  code: string;
  name: string;
  category: string;
  description: string;
  price: string;
  quantity: number;
  total_price: string;
  external_code: string;
  tags: ExtraTag;
  uuid: string;
}

export interface ExtraWithPassenger extends Extra {
  passengerName: string;
}

export interface ReservationGuestPricingInterface {
  category: string;
  extras: string;
  fare: string;
  gft: string;
  guest: number;
  total_fare: string;
  total_price: number;
  discount: string;
}

export interface ReservationGuestInterface {
  address: string[];
  city: string;
  country: string;
  date_of_birth: string;
  documentation: [];
  email: string;
  external_code: string | null;
  extras: ExtraWithPassenger[];
  gender: string;
  given_name: string;
  id: number;
  language: string;
  lastname: string;
  lead_passenger: boolean;
  nationality: string;
  passenger_type: string;
  phone: string;
  pricing: ReservationGuestPricingInterface;
  request: string;
  state: string;
  title: string;
  uuid: string;
  zip_code: string;
  loyalty_number: string;
}

export interface ReservationCabinsInterface {
  [x: string]: any;
  guests: ReservationGuestInterface[];
  cabin_number: string;
  uuid: string;
  grade_code: string;
  deck_name: string;
}

export interface ReservationItineraryInterface {
  arrive_time: string;
  date: string;
  depart_time: string;
  extra_information: string | null;
  port_code: string;
  port_country: string;
  port_name: string;
  tender: boolean;
}

export interface ReservationSailingsInterface {
  cabins: ReservationCabinsInterface[];
  commission: string;
  consortia_commission: string;
  cruise_code: string;
  cruise_duration: number;
  cruise_name: string;
  currency: string;
  embarkation_date: string;
  external_rate_code: string | null;
  external_sailing_code: string;
  extras_price: string;
  fare: string;
  itinerary: ReservationItineraryInterface[];
  price: string;
  rate_code: string;
  rate_name: string;
  sailing_code: string;
  ship_code: string;
  ship_name: string;
}

export interface ReservationPaymentScheduleInterface {
  amount: number;
  currency: string;
  due_date: string;
}

export interface TransactionInterface {
  id: number;
  currency: string;
  amount: string;
  made_on: string;
  made_by: string;
  transaction_type: string;
  card_clearance: {
    other: {
      card_type: string;
      last_four: string;
    };
    auth_code: string;
    transaction_id: string;
    clearance_system: string;
  };
  refunded: boolean;
}

export interface FlightLeg {
  id: number;
  uuid: string;
  flight_number: string;
  flight_operator: string | null;
  airline: string | null;
  departure_airport: string;
  departure_airport_name: string | null;
  departure_datetime: string | null;
  arrival_airport: string;
  arrival_airport_name: string | null;
  arrival_datetime: string | null;
  sequence: number;
}

export interface Flight {
  id: number;
  uuid: string;
  currency: string;
  fare: string;
  price: string;
  commission: string;
  flight_pnr: string | null;
  legs: FlightLeg[];
}

export interface ReservationListInterface {
  agency_name: string;
  pnr: string;
  external_pnr: string;
  lead_given_name: string;
  embarkation_date: string;
  cruise_name: string;
  ship_name: string;
  created: string;
  currency: string;
  total_price: number;
  total_paid: number;
  lead_email?: string;
  lead_lastname: string;
  lead_phone?: string;
  lead_title?: string;
  sailings: ReservationSailingsInterface[];
  payment_schedule: ReservationPaymentScheduleInterface[];
  market: string;
  commission: string;
  next_payment_outstanding: string;
  next_payment_due: string;
  transactions: TransactionInterface[];
  status: string;
  flights?: Flight[];
}

export interface ReservationInterface {
  agency_name: string;
  status: string;
  pnr: string;
  external_pnr: string;
  lead_given_name: string;
  booking_date: string;
  embarkation_date: string;
  cruise_name: string;
  ship_name: string;
  created: string;
  currency: string;
  total_price: number;
  total_paid: number;
  lead_email?: string;
  lead_lastname: string;
  lead_phone?: string;
  lead_title?: string;
  sailings: ReservationSailingsInterface[];
  payment_schedule: ReservationPaymentScheduleInterface[];
  market: string;
  commission: string;
  next_payment_outstanding: string;
  next_payment_due: string;
  transactions: TransactionInterface[];
  flights?: Flight[];
}

export interface AvailableGradeFormatted {
  code: string;
  name: string;
  category: string;
  total_price: number;
  available_cabins: number;
}

export interface AvailableCabinFormatted {
  uuid: string;
  number: string;
  deck_name: string;
  deck_code: string;
  grade_code: string;
  meta_category: string;
  maximum_occupancy: string;
  location: string;
  position: string;
  cabin_size: string;
  cabin_measurement: string;
}

export interface ReservationState {
  reservationList: ReservationListResponse;
  availableGrades: AvailableGradeFormatted[];
  availableCabins: AvailableCabinFormatted[];
  reservation: ReservationListInterface;
  selectedGrade: string;
  selectedCabin: string;
}

const initialState: ReservationState = {
  selectedGrade: "",
  selectedCabin: "",
  reservationList: [],
  availableGrades: [],
  availableCabins: [],
  reservation: {
    agency_name: "",
    status: "",
    pnr: "",
    external_pnr: "",
    lead_given_name: "",
    embarkation_date: "",
    ship_name: "",
    cruise_name: "",
    created: "",
    currency: "",
    total_price: 0,
    total_paid: 0,
    lead_email: "",
    lead_lastname: "",
    lead_phone: "",
    lead_title: "",
    sailings: [
      {
        cabins: [
          {
            guests: [
              {
                address: [""],
                city: "",
                country: "",
                date_of_birth: "",
                documentation: [],
                email: "",
                external_code: "",
                extras: [],
                gender: "",
                given_name: "",
                id: 0,
                language: "",
                lastname: "",
                lead_passenger: false,
                nationality: "",
                passenger_type: "",
                phone: "",
                pricing: {
                  category: "",
                  extras: "",
                  fare: "",
                  gft: "",
                  discount: "",
                  guest: 0,
                  total_fare: "",
                  total_price: 0,
                },
                request: "",
                state: "",
                title: "",
                uuid: "",
                zip_code: "",
                loyalty_number: "",
              },
            ],
            cabin_number: "",
            uuid: "",
            grade_code: "",
            deck_name: "",
          },
        ],
        commission: "",
        consortia_commission: "",
        cruise_code: "",
        cruise_duration: 0,
        cruise_name: "",
        currency: "USD",
        embarkation_date: "",
        external_rate_code: "",
        external_sailing_code: "",
        extras_price: "",
        fare: "",
        itinerary: [],
        price: "",
        rate_code: "",
        rate_name: "",
        sailing_code: "",
        ship_code: "",
        ship_name: "",
      },
    ],
    payment_schedule: [{ amount: 0, currency: "", due_date: "" }],
    market: "",
    commission: "",
    next_payment_outstanding: "",
    next_payment_due: "",
    transactions: [],
    flights: [],
  },
};

const reservationSlice = createSlice({
  name: "reservation",
  initialState,
  reducers: {
    setSelectedGrade(state: ReservationState, action: PayloadAction<string>) {
      state.selectedGrade = action.payload;
    },

    setSelectedCabin(state: ReservationState, action: PayloadAction<string>) {
      state.selectedCabin = action.payload;
    },

    resetAvailableCabins(state: ReservationState) {
      state.availableCabins = [];
    },

    resetEditCabinStateToInitial(state: ReservationState) {
      return {
        ...state,
        availableGrades: [],
        availableCabins: [],
        selectedGrade: "",
        selectedCabin: "",
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      ReservationApi.endpoints.initReservationList.matchFulfilled,
      (state, action) => {
        state.reservationList = action.payload;
      },
    );

    builder.addMatcher(
      ReservationApi.endpoints.retrieveReservation.matchFulfilled,
      (state, action) => {
        state.reservation = action.payload;
      },
    );

    builder.addMatcher(
      ReservationApi.endpoints.retrieveAvailableGrades.matchFulfilled,
      (state, action) => {
        const formattedGrades: AvailableGradeFormatted[] =
          action?.payload?.grades?.map((grade) => ({
            code: grade.code,
            name: grade.name?.["en-us"] ?? grade.code,
            category: grade.meta_category,
            total_price: grade.total_price,
            available_cabins: 0,
          })) ?? [];

        state.availableGrades = formattedGrades;
      },
    );

    builder.addMatcher(
      ReservationApi.endpoints.retrieveAvailableCabins.matchFulfilled,
      (state, action) => {
        const formattedCabins: AvailableCabinFormatted[] =
          action?.payload?.map((cabin) => ({
            uuid: `${state.selectedGrade}-${cabin.number}`,
            number: cabin.number,
            deck_name: cabin.deck_name,
            deck_code: cabin.deck_code ?? "",
            grade_code: state.selectedGrade,
            meta_category: state.selectedGrade
              ? (state.availableGrades.find(
                  (g) => g.code === state.selectedGrade,
                )?.category ?? "")
              : "",
            maximum_occupancy: cabin.max_occupancy,
            location: cabin.location,
            position: cabin.position,
            cabin_size: cabin.cabin_size,
            cabin_measurement: cabin.cabin_measurement,
          })) ?? [];

        state.availableCabins = formattedCabins;
      },
    );
  },
});

export const {
  setSelectedGrade,
  setSelectedCabin,
  resetAvailableCabins,
  resetEditCabinStateToInitial,
} = reservationSlice.actions;

export default reservationSlice.reducer;
